import { useEffect, useState } from "react";
import { toast } from "sonner";

import env from "@/valid-env";

import useAuthStore from "./auth";

interface SafeHavenCheckoutOptions {
	id?: string;
	environment?: "production" | "sandbox";
	clientId: string;
	referenceCode?: string;
	customer: {
		firstName: string;
		lastName: string;
		emailAddress: string;
		phoneNumber: string;
	};
	currency: "NGN";
	amount: number;
	feeBearer?: "account" | "customer";
	customIconUrl?: string;
	redirectUrl?: string;
	webhookUrl?: string;
	removeBranding?: "true" | "false";
	settlementAccount?: {
		bankCode: string;
		accountNumber: string;
	};
	metadata?: Record<string, any>;
	onClose?: () => void;
	callback?: (response: { referenceCode: string }) => void;
}

declare function SafeHavenCheckout(options: SafeHavenCheckoutOptions): void;

export function useSafeHaven() {
	const [isInitialized, setIsInitialized] = useState(false);

	const user = useAuthStore(state => state.user);

	if (!user)
		throw new Error("User not found");

	useEffect(() => {
		const script = document.createElement("script");
		script.src = "https://checkout.safehavenmfb.com/assets/checkout.min.js";
		script.async = true;
		script.onload = () => setIsInitialized(true);
		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	const initializePayment = async ({ callback, customer, amount, referenceCode, clientId, settlementAccount }: {
		amount: number;
		referenceCode: string;
		clientId: string;
		settlementAccount: {
			bankCode: string;
			accountNumber: string;
		};
		customer: {
			firstName: string;
			lastName: string;
			emailAddress: string;
			phoneNumber: string;
		};
		callback: (response: { referenceCode: string }) => void;

	}) => {
		if (!isInitialized)
			throw new Error("SafeHaven Checkout not initialized");

		SafeHavenCheckout({
			amount,
			clientId,
			customer,
			referenceCode,
			currency: "NGN",
			settlementAccount,
			feeBearer: "customer",
			environment: env.VITE_SAFEHAVEN_ENV,
			onClose: () => toast.warning("Payment cancelled"),
			callback
		});
	};

	return { initializePayment, isInitialized };
}
